<script setup>
import { defineProps, defineEmits, ref, watch, onMounted } from 'vue'

import { config } from '@/config'

const props = defineProps({
  isVisible: Boolean,
  type: String,
  amount: Number,
  currency: String,
  description: String,
  publicId: String,
  orderId: Number,
  uid: Number,
  paymentSchema: String
})

const emit = defineEmits(['close', 'paid-success'])

const status = ref('pending')
const isClosing = ref(false)
let widget = null

let retryCount = 0
const maxRetries = 10

async function startPayment() {
  try {
     if (props.uid === 0) {
      retryCount++
      console.log(retryCount, "retryCount")
      if (retryCount > maxRetries) {
        status.value = 'error'
        onClose()
        return
      }
      setTimeout(() => startPayment(), 300)
      return
    }

    if (!window.cp || !window.cp.CloudPayments) {
      console.error('CloudPayments not loaded')
      return
    }

    widget = new window.cp.CloudPayments()

    const intentParams = {
      publicTerminalId: props.publicId,
      amount: props.amount,
      currency: props.currency,
      description: props.description,
      externalId: props.orderId.toString(),
      accountId: String(props.uid),
      skin: 'classic',
      paymentSchema: props.paymentSchema,
      userInfo: { accountId: String(props.uid) },
      metadata: { order_id: props.orderId },
      data: { order_id: props.orderId },
      restrictedPaymentMethods: ['Sbp'],
      autoClose: 3
    }
    status.value = 'pending'
    const result = await widget.start(intentParams)

    if (result.status) {
      status.value = 'success'
      ym(config.yandexMetrikaId, 'reachGoal', 'pay')
       emit('paid-success')
      setTimeout(() => onClose(), 1000)
    } else {
      status.value = 'failed'
      setTimeout(() => onClose(), 1000)
    }
  } catch (error) {
    console.error('Payment error:', error)
    status.value = 'failed'
    setTimeout(() => {
      widget?.close()
      onClose()
    }, 1000)
  }
}

watch(() => props.isVisible, (visible) => {
  if (visible) {
    status.value = 'pending'
    isClosing.value = false
    startPayment()
  } else {
    if (widget) {
      widget.close()
      widget = null
    }
    status.value = 'pending'
    isClosing.value = false
  }
})

onMounted(() => {
  if (props.isVisible) {
    status.value = 'pending'
    isClosing.value = false
    startPayment()
  }
})

function onClose() {
  if (isClosing.value) return

  isClosing.value = true

  setTimeout(() => {
    if (status.value === 'success') {
      emit('paid-success')
    }
    emit('close')
    isClosing.value = false
  }, 300)
}
</script>

<template>
  <div v-if="isVisible" class="modal-fullscreen-overlay" :class="{ closing: isClosing }">
    <div class="loader-container">
      <div class="close-button" @click="onClose">
        <img src="@/assets/images/android/close.svg" alt="Close" class="close-icon" />
      </div>

      <div v-if="status === 'pending'" class="payment-content">
        <p class="loading-text">Перенапралвение на страницу оплаты...</p>
      </div>

      <div v-else-if="status === 'success'" class="status-content">
        <div class="status-image success">✓</div>
        <p class="loading-text">Платёж прошёл успешно</p>
      </div>

      <div v-else-if="status === 'failed'" class="status-content">
        <div class="status-image failed">✗</div>
        <p class="loading-text">Платёж не прошёл</p>
      </div>

      <div v-else-if="status === 'error'" class="status-content">
        <div class="status-image failed">✗</div>
        <p class="loading-text">Ошибка! Повторите попытку</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.loader-container {
  background: white;
  position: relative;
  text-align: center;
  width: 90%;
  max-width: 600px;
  height: 80vh;
  max-height: 800px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: auto;
  padding: 20px;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .loader-container {
    width: 100%;
    height: 100%;
    max-width: none;
    max-height: none;
    margin: 0;
    border-radius: 0;
    padding: 0;
  }

  .payment-iframe {
    height: calc(100% - 60px);
    margin-bottom: 10px;
    border-radius: 0;
  }

  .close-button {
    top: env(safe-area-inset-top, 10px);
    right: 10px;
  }

  .modal-fullscreen-overlay {
    background: white;
  }
}

.payment-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.payment-content > * {
  transform: translateY(-50px);
}

.status-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.loader {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #3498db;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 16px 0;
}

.status-image {
  font-size: 60px;
  margin-bottom: 16px;
}

.status-image.success {
  color: blue;
}

.status-image.failed {
  color: red;
}

.loading-text {
  font-size: 16px;
  color: #333;
  margin: 0;
}

.close-button {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  z-index: 1000;
}

.close-icon {
  width: 24px;
  height: 24px;
}

.modal-fullscreen-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.slide-up-enter-active, .slide-up-leave-active {
  transition: transform 0.4s ease, opacity 0.3s ease;
}

.slide-up-enter-from, .slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}

.slide-up-enter-to, .slide-up-leave-from {
  transform: translateY(0%);
  opacity: 1;
}
</style>
